import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
    input,
    inject,
    signal,
    computed,
    Injector,
    EnvironmentInjector,
} from '@angular/core';
import { BazisModalService } from '@bazis/shared/services/modal.service';
import { Modal } from '@bazis/shared/models/modal.types';
import { CommonModule } from '@angular/common';
import { ButtonModule } from '@bazis/shared/components/web/button/button.module';
import { IconModule } from '@bazis/shared/components/web/icon/icon.module';

@Component({
    selector: 'bazis-modal',
    templateUrl: './modal.component.html',
    imports: [CommonModule, ButtonModule, IconModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent implements OnDestroy, OnInit {
    private readonly injector = inject(Injector);
    private readonly environmentInjector = inject(EnvironmentInjector);
    public modalService = inject(BazisModalService);

    readonly modal = input<Modal>();

    private readonly externalInjector = computed(() => {
        const modal = this.modal();

        if (!modal || !modal.options || !modal.options.injector) return this.injector;

        return modal.options.injector;
    });

    private readonly externalEnvironmentInjector = computed(() => {
        const modal = this.modal();

        if (!modal || !modal.options || !modal.options.injector) return this.environmentInjector;

        return modal.options.environmentInjector;
    });

    componentRef: ComponentRef<any>;

    isScrollingContent: boolean = false;

    @ViewChild('componentHost', { read: ViewContainerRef, static: true })
    componentHost!: ViewContainerRef;

    @HostListener('document:keydown.escape') onKeydownHandler() {
        if (this.modal().options.keyboardClose) {
            this.close();
        }
    }

    ngOnInit(): void {
        this.loadComponent();
    }

    ngOnDestroy(): void {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }

    close() {
        if (this.componentRef.instance.close) {
            this.componentRef.instance.close();
        } else {
            this.modalService.dismiss(null, this.modal());
        }
    }

    loadComponent() {
        const component = this.modal().options.component;
        this.componentHost.clear();
        this.componentRef = this.componentHost.createComponent(component, {
            injector: this.externalInjector(),
            environmentInjector: this.externalEnvironmentInjector(),
        });
        const modal = this.modal();
        if (modal.options.componentProperties) {
            Object.keys(modal.options.componentProperties).forEach((key) => {
                // TEMP - remove if, remain only else part when will support signals in all applications
                if (
                    typeof this.componentRef.instance[key] === 'function' &&
                    this.modal().options.componentProperties[key] !== undefined &&
                    typeof this.modal().options.componentProperties[key] !== 'function'
                ) {
                    this.componentRef.instance[key] = signal(
                        this.modal().options.componentProperties[key],
                    );
                } else {
                    this.componentRef.instance[key] = this.modal().options.componentProperties[key];
                }
            });
        }
    }

    closeFromBackdrop() {
        if (this.modal().options.backdropClose) {
            this.close();
        } else return null;
    }

    onContentScroll(ev) {
        const scrollPosition = ev.target.scrollTop;
        this.isScrollingContent = scrollPosition > 100 ? true : false;
    }
}
